/**
 * Session Manager — Enforces one web session + one app session per user.
 *
 * On login, registers the session with the server. The server stores
 * the active session ID in Clerk private_metadata and revokes any
 * other sessions of the same platform type.
 *
 * Validates the session:
 *  - Every 10 seconds via polling
 *  - Immediately when the browser tab becomes visible (user switches back)
 *  - Immediately on page load
 *
 * If another device has logged in, forces logout on this device.
 */

import axios from "axios";

const API_BASE = process.env.VUE_APP_API_BASE || "";
const SESSION_ID_KEY = "tc_active_session_id";
const PLATFORM_KEY = "tc_session_platform";
const VALIDATE_INTERVAL_MS = 10 * 1000; // Check every 10 seconds

let _validateTimer = null;
let _onForceLogout = null;
let _getToken = null;
let _visibilityHandler = null;
let _isValidating = false;

/**
 * Generate a unique session ID for this browser instance.
 */
function generateSessionId() {
  const ts = Date.now().toString(36);
  const rand = crypto && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
  return `sess_${ts}_${rand}`;
}

/**
 * Get or create the session ID for this browser.
 * Stored in localStorage so all tabs in the same browser share one session.
 */
function getSessionId() {
  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = generateSessionId();
    localStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

/**
 * Force a new session ID (used on fresh login to ensure uniqueness).
 */
function resetSessionId() {
  const id = generateSessionId();
  localStorage.setItem(SESSION_ID_KEY, id);
  return id;
}

/**
 * Get the platform type. Defaults to "web" for browser sessions.
 */
function getPlatform() {
  return localStorage.getItem(PLATFORM_KEY) || "web";
}

/**
 * Set the platform type (call from mobile app wrapper if needed).
 * @param {"web" | "app"} platform
 */
export function setPlatform(platform) {
  localStorage.setItem(PLATFORM_KEY, platform);
}

/**
 * Register this session with the server.
 * Should be called after a FRESH login (not page refresh).
 * Generates a new session ID and kicks other devices.
 *
 * @param {Function} getTokenFn - async function that returns the auth token
 * @param {Function} onForceLogoutFn - called when session is invalidated
 * @param {"web" | "app"} [platform] - override platform detection
 */
export async function registerSession(getTokenFn, onForceLogoutFn, platform) {
  _getToken = getTokenFn;
  _onForceLogout = onForceLogoutFn;

  // Generate a fresh session ID on every registration to ensure
  // the new login always gets a unique ID that supersedes the old one
  const sessionId = resetSessionId();
  const plat = platform || getPlatform();

  try {
    const token = await getTokenFn();
    if (!token) return;

    const { data } = await axios.post(
      `${API_BASE}/api/session/register`,
      { platform: plat, sessionId },
      { headers: { Authorization: `Bearer ${token}` } }
    );


    // Start periodic validation + visibility listener
    startValidation();
    startVisibilityListener();
  } catch (err) {
    console.warn("[SessionManager] Failed to register session:", err.message);
    // Non-fatal — don't block the user from using the app
    // Still start validation so if registration eventually works, we catch invalidation
    startValidation();
    startVisibilityListener();
  }
}

/**
 * Start session validation WITHOUT registering a new session.
 * Used on page refresh when the user already has a stored session.
 * If the session was invalidated by another device, forces logout.
 *
 * @param {Function} getTokenFn - async function that returns the auth token
 * @param {Function} onForceLogoutFn - called when session is invalidated
 * @param {"web" | "app"} [platform] - override platform detection
 */
export async function resumeSession(getTokenFn, onForceLogoutFn, platform) {
  _getToken = getTokenFn;
  _onForceLogout = onForceLogoutFn;

  const sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    // No stored session — treat as fresh login
    return registerSession(getTokenFn, onForceLogoutFn, platform);
  }

  // Start validation and visibility listener
  startValidation();
  startVisibilityListener();

  // Do an immediate validation check
  await validateSession();
}

/**
 * Start periodic session validation.
 */
function startValidation() {
  stopValidation();
  _validateTimer = setInterval(validateSession, VALIDATE_INTERVAL_MS);
}

/**
 * Stop periodic session validation.
 */
function stopValidation() {
  if (_validateTimer) {
    clearInterval(_validateTimer);
    _validateTimer = null;
  }
}

/**
 * Listen for page visibility changes.
 * When user switches back to this tab, immediately validate.
 */
function startVisibilityListener() {
  stopVisibilityListener();
  _visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      // User came back to this tab — check immediately
      validateSession();
    }
  };
  document.addEventListener("visibilitychange", _visibilityHandler);
}

function stopVisibilityListener() {
  if (_visibilityHandler) {
    document.removeEventListener("visibilitychange", _visibilityHandler);
    _visibilityHandler = null;
  }
}

/**
 * Validate the current session with the server.
 * If the session has been replaced, triggers force logout.
 */
async function validateSession() {
  if (!_getToken || _isValidating) return;
  _isValidating = true;

  const sessionId = localStorage.getItem(SESSION_ID_KEY);
  const platform = getPlatform();

  if (!sessionId) {
    _isValidating = false;
    return;
  }

  try {
    const token = await _getToken();
    if (!token) {
      _isValidating = false;
      return;
    }

    const { data } = await axios.post(
      `${API_BASE}/api/session/validate`,
      { platform, sessionId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (data.valid === false) {
      console.warn("[SessionManager] Session invalidated:", data.reason);
      stopValidation();
      stopVisibilityListener();

      if (_onForceLogout) {
        _onForceLogout(data.message || "Your session was ended because you logged in from another device.");
      }
    }
  } catch (err) {
    // Network errors — don't force logout, just skip this check
    console.warn("[SessionManager] Validation check failed:", err.message);
  } finally {
    _isValidating = false;
  }
}

/**
 * Notify the server that this session is ending (user-initiated logout).
 */
export async function clearSession() {
  stopValidation();
  stopVisibilityListener();

  const platform = getPlatform();

  try {
    if (_getToken) {
      const token = await _getToken();
      if (token) {
        await axios.post(
          `${API_BASE}/api/session/logout`,
          { platform },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    }
  } catch (err) {
    // Non-fatal
    console.warn("[SessionManager] Failed to clear session on server:", err.message);
  }

  localStorage.removeItem(SESSION_ID_KEY);
  _getToken = null;
  _onForceLogout = null;
}

/**
 * Check if session management is active.
 */
export function isSessionActive() {
  return _validateTimer !== null;
}

export default {
  registerSession,
  resumeSession,
  clearSession,
  setPlatform,
  isSessionActive,
  getSessionId,
  getPlatform,
};
